import React from 'react'
import Axios from 'axios'
import { getSessionId, init, logEvent, logException, setEntityId } from '@src/utils/logClient'
import { checkRedirect } from '@src/utils/routes'

const entityMap = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
  '/': '&#x2F;'
}

const escapeHtml = function(str) {
  // eslint-disable-next-line no-useless-escape
  return String(str).replace(/[&<>"'\/]/g, function(s) {
    return entityMap[s]
  })
}

const isFromUs = function(headers) {
  const header = headers['x-usa'] || headers['X-USA']
  return header && Number(header) === 1
}

const sessionUrlOrigin = function() {
  const location = window.location
  let origin = location.origin
  if (process.env.GATSBY_ENV === 'development') origin = `${location.protocol}//${location.hostname}:8001`
  return origin
}

const isBadParentCodePath = path => /^[pP]{1}.+/.test(path) && path !== 'proudtoteach' && path !== 'privacy' && path !== 'playvideo'

const setOnError = () => {
  window.onerror = async function(msg, url, lineNo, colNo, error: any) {
    let context
    if (!url || /classdojo/gi.test(url)) {
      context = 'script: ' + url + ', lineNo: ' + lineNo + ', colNo: ' + colNo
      if (!error) {
        error = {
          message: msg,
          status: 'unknown',
          method: 'unknown',
          url: url,
          stack: context
        }
      }
      if (Math.random() < 0.1) {
        return (await logException)(error, context)
      }
    }
  }
}

type AppData = {
  loadingSession?: boolean
  type?: string
  fromUS?: boolean
}
type AppDataContextProps = {data: AppData, set: (AppData) => void}

const defaultContextValue = {
  data: { loadingSession: true, type: undefined, fromUS: undefined },
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  set: () => {}
}

const AppDataContext = React.createContext<AppDataContextProps>(defaultContextValue)

class AppDataContextProvider extends React.Component<{}, AppDataContextProps> {
  constructor(props) {
    super(props)

    this.setData = this.setData.bind(this)
    this.state = {
      ...defaultContextValue,
      set: this.setData
    }
  }

  componentDidMount() {
    checkRedirect() //redirect to desired locale
    init({
      site: 'external',
      env: process.env.GATSBY_ENV === 'development' ? 'dev' : 'prod'
    })
    if (typeof window !== 'undefined') {
      (window as any).sessionId = getSessionId()
    }

    setOnError()

    this.getSession()
  }

  getSession() {
    const SESSION = `${sessionUrlOrigin()}/api/session`
    Axios.get(SESSION, { withCredentials: true })
      .then(response => {
        // console.log('session response', response)
        const data: any = { fromUS: isFromUs(response.headers) }
        const userData = response.data
        if ('type' in userData) {
          data.type = userData.type
          if (userData.type === 'teacher') {
            data.name = escapeHtml(userData.teacher.firstName)
          } else if (userData.type === 'parent') {
            data.name = escapeHtml(userData.parent.firstName)
          }
        }
        this.setData(data)

        const entity = userData.teacher || userData.parent || userData.student
        setEntityId(entity._id)

        const routeName =
          window.location.pathname
            .slice(1)
            .split('/')
            .shift() || 'index'
        if (isBadParentCodePath(routeName)) logEvent('user_visit:bad_parent_code_path')
        else logEvent(`user_visit:${routeName}`)
      })
      .catch(error => {
        this.setData({ fromUS: error.response ? isFromUs(error.response.headers) : false })
      })
      .finally(() => {
        this.setData({ loadingSession: false })
      })
  }

  setData(newData) {
    this.setState((state: AppDataContextProps) => ({
      data: {
        ...state.data,
        ...newData
      }
    }))
  }

  render() {
    return <AppDataContext.Provider value={this.state}>{this.props.children}</AppDataContext.Provider>
  }
}

export { AppDataContext, AppDataContextProvider }
